import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import {
  Plus,
  FolderKanban,
  MoreHorizontal,
  Pencil,
  Trash2,
  Archive,
  ArchiveRestore,
} from 'lucide-react';
import { Header, PageContainer } from '@/components/layout';
import {
  Button,
  Card,
  CardBody,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell,
  StatusBadge,
  Badge,
  EmptyState,
  Input,
  Select,
  Dropdown,
  DropdownItem,
  ConfirmDialog,
} from '@/components/ui';
import { ProjectFormModal } from '@/components/forms';
import {
  useProjects,
  useDeleteProject,
  useArchiveProject,
  useUnarchiveProject,
} from '@/hooks/useProjects';
import { useAuthStore } from '@/stores/auth';
import { formatDate } from '@aelita/shared';
import type { Project } from '@aelita/shared';

export const Route = createFileRoute('/projects')({
  component: ProjectsPage,
});

function ProjectsPage() {
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [deletingProject, setDeletingProject] = useState<Project | null>(null);
  const [archivingProject, setArchivingProject] = useState<Project | null>(null);
  const [filters, setFilters] = useState<{ status?: string; search?: string }>({});
  const { data, isLoading } = useProjects(filters);
  const deleteProject = useDeleteProject();
  const archiveProject = useArchiveProject();
  const unarchiveProject = useUnarchiveProject();
  const projects = data?.data ?? [];
  const user = useAuthStore((s) => s.user);

  // Role checks
  const isAdmin = user?.role === 'admin';
  const isManager = user?.role === 'admin' || user?.role === 'manager' || user?.role === 'pm';

  const handleDelete = async () => {
    if (!deletingProject) return;
    try {
      await deleteProject.mutateAsync(deletingProject.id);
      setDeletingProject(null);
    } catch (error) {
      console.error('Failed to delete project:', error);
    }
  };

  const handleArchive = async () => {
    if (!archivingProject) return;
    try {
      if (archivingProject.status === 'archived') {
        await unarchiveProject.mutateAsync(archivingProject.id);
      } else {
        await archiveProject.mutateAsync(archivingProject.id);
      }
      setArchivingProject(null);
    } catch (error) {
      console.error('Failed to archive/unarchive project:', error);
    }
  };

  return (
    <>
      <Header
        title="Projects"
        subtitle="Manage your client and internal projects"
        actions={
          <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setIsCreateOpen(true)}>
            New Project
          </Button>
        }
      />
      <PageContainer>
        {/* Filters */}
        <Card className="mb-6">
          <CardBody className="flex items-center gap-4">
            <Input
              placeholder="Search projects..."
              className="max-w-xs"
              value={filters.search ?? ''}
              onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
            />
            <Select
              options={[
                { value: '', label: 'All Statuses' },
                { value: 'pipeline', label: 'Pipeline' },
                { value: 'active', label: 'Active' },
                { value: 'on_hold', label: 'On Hold' },
                { value: 'completed', label: 'Completed' },
                { value: 'cancelled', label: 'Cancelled' },
                { value: 'archived', label: 'Archived' },
              ]}
              value={filters.status ?? ''}
              onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value || undefined }))}
              className="w-40"
            />
          </CardBody>
        </Card>

        {/* Projects Table */}
        <Card>
          {isLoading ? (
            <CardBody>
              <p className="text-gray-500 dark:text-gray-400">Loading projects...</p>
            </CardBody>
          ) : projects.length === 0 ? (
            <EmptyState
              icon={FolderKanban}
              title="No projects yet"
              description="Create your first project to get started"
              action={{
                label: 'Create Project',
                onClick: () => setIsCreateOpen(true),
              }}
            />
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeader>Project</TableHeader>
                  <TableHeader>Client</TableHeader>
                  <TableHeader>Status</TableHeader>
                  <TableHeader>Type</TableHeader>
                  <TableHeader>Start Date</TableHeader>
                  <TableHeader>Tasks</TableHeader>
                  <TableHeader className="w-12">{''}</TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {projects.map((project: any) => (
                  <TableRow key={project.id}>
                    <TableCell>
                      <Link
                        to="/project/$projectId"
                        params={{ projectId: project.id }}
                        className="font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
                      >
                        {project.name}
                      </Link>
                      {project.description && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-xs">
                          {project.description}
                        </p>
                      )}
                    </TableCell>
                    <TableCell>
                      {project.client?.name ?? <span className="text-gray-400">Internal</span>}
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={project.status} />
                    </TableCell>
                    <TableCell>
                      <Badge variant="gray">{project.type}</Badge>
                    </TableCell>
                    <TableCell>
                      {project.startDate ? formatDate(new Date(project.startDate)) : '-'}
                    </TableCell>
                    <TableCell>{project._count?.tasks ?? 0}</TableCell>
                    <TableCell>
                      <Dropdown
                        trigger={
                          <button className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded">
                            <MoreHorizontal className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                          </button>
                        }
                        align="right"
                        width="w-40"
                      >
                        <DropdownItem onClick={() => setEditingProject(project)}>
                          <div className="flex items-center gap-2">
                            <Pencil className="h-4 w-4" />
                            Edit
                          </div>
                        </DropdownItem>
                        {isManager && (
                          <DropdownItem onClick={() => setArchivingProject(project)}>
                            <div className="flex items-center gap-2">
                              {project.status === 'archived' ? (
                                <>
                                  <ArchiveRestore className="h-4 w-4" />
                                  Unarchive
                                </>
                              ) : (
                                <>
                                  <Archive className="h-4 w-4" />
                                  Archive
                                </>
                              )}
                            </div>
                          </DropdownItem>
                        )}
                        {isAdmin && (
                          <DropdownItem
                            onClick={() => setDeletingProject(project)}
                            className="text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950"
                          >
                            <div className="flex items-center gap-2">
                              <Trash2 className="h-4 w-4" />
                              Delete
                            </div>
                          </DropdownItem>
                        )}
                      </Dropdown>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>
      </PageContainer>

      {/* Create Modal */}
      <ProjectFormModal
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        project={null}
      />

      {/* Edit Modal */}
      <ProjectFormModal
        isOpen={!!editingProject}
        onClose={() => setEditingProject(null)}
        project={editingProject}
      />

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={!!deletingProject}
        onClose={() => setDeletingProject(null)}
        onConfirm={handleDelete}
        title="Delete Project"
        message={`Are you sure you want to permanently delete "${deletingProject?.name}"? This will also delete all associated tasks and time entries. This action cannot be undone.`}
        confirmLabel="Delete Permanently"
        isLoading={deleteProject.isPending}
      />

      {/* Archive Confirmation */}
      <ConfirmDialog
        isOpen={!!archivingProject}
        onClose={() => setArchivingProject(null)}
        onConfirm={handleArchive}
        title={archivingProject?.status === 'archived' ? 'Unarchive Project' : 'Archive Project'}
        message={
          archivingProject?.status === 'archived'
            ? `Are you sure you want to restore "${archivingProject?.name}"? It will be set to Active status.`
            : `Are you sure you want to archive "${archivingProject?.name}"? It will be hidden from active projects but can be restored later.`
        }
        confirmLabel={archivingProject?.status === 'archived' ? 'Unarchive' : 'Archive'}
        isLoading={archiveProject.isPending || unarchiveProject.isPending}
      />
    </>
  );
}
